import { useState, useEffect, useCallback } from 'react';
import { 
  subscribeToRooms, 
  updateRoomStatus as firebaseUpdateRoomStatus,
  checkInGuest as firebaseCheckInGuest,
  checkOutGuest as firebaseCheckOutGuest
} from '../services/firebase';
import { Room, HotelStatistics } from '../services/roomApiService';

interface Analytics {
  period: string;
  generated_at: string;
  occupancy: HotelStatistics;
  revenue: {
    today: number;
    month: number;
    average_per_room: number;
    projected_monthly: number;
  };
  bookings: {
    total: number;
    average_rating: number;
    completion_rate: number;
  };
  room_types: Record<string, any>;
  forecast: Array<{
    date: string;
    day_name: string;
    predicted_occupancy: number;
    occupancy_rate: number;
    estimated_revenue: number;
  }>;
  maintenance: {
    rooms_needing_attention: number;
    last_deep_clean: string;
    next_scheduled: string;
  };
}

export function useRoomManagement() {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [statistics, setStatistics] = useState<HotelStatistics>({
    total_rooms: 27,
    available: 0,
    occupied: 0,
    cleaning: 0,
    maintenance: 0,
    out_of_order: 0,
    occupancy_rate: 0
  });
  const [analytics] = useState<Analytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(true);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  // Subscribe to real-time room updates
  useEffect(() => {
    setLoading(true);
    
    const unsubscribe = subscribeToRooms((roomsData: Room[]) => {
      setRooms(roomsData);
      updateStatistics(roomsData);
      setLastUpdate(new Date());
      setLoading(false);
      setIsConnected(true);
    });

    // Cleanup subscription on unmount
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // Update statistics based on room data
  const updateStatistics = (roomsData: Room[]) => {
    const stats = {
      total_rooms: roomsData.length,
      available: roomsData.filter(r => r.status === 'available').length,
      occupied: roomsData.filter(r => r.status === 'occupied').length,
      cleaning: roomsData.filter(r => r.status === 'cleaning').length,
      maintenance: roomsData.filter(r => r.status === 'maintenance').length,
      out_of_order: roomsData.filter(r => r.status === 'out_of_order').length,
      occupancy_rate: Math.round((roomsData.filter(r => r.status === 'occupied').length / roomsData.length) * 100)
    };
    setStatistics(stats);
  };

  const refreshData = useCallback(async () => {
    try {
      setError(null);
      setLoading(true);
      // Firebase automatically refreshes via subscription
      setLastUpdate(new Date());
    } catch (err) {
      setError('Failed to refresh data');
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateRoomStatus = async (roomNumber: string, status: Room['status'], notes?: string) => {
    try {
      setError(null);
      const success = await firebaseUpdateRoomStatus(roomNumber, status, notes);
      if (!success) {
        throw new Error('Failed to update room status');
      }
    } catch (err) {
      setError('Failed to update room status');
      setIsConnected(false);
    }
  };

  const checkInGuest = async (roomNumber: string, guestInfo: any) => {
    try {
      setError(null);
      const success = await firebaseCheckInGuest(roomNumber, guestInfo);
      
      if (success) {
        // Show success notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Hotel A.S.K Grand', {
            body: `Guest ${guestInfo.name} checked in to Room ${roomNumber}`,
            icon: '/Logo.png',
          });
        }
      } else {
        throw new Error('Failed to check in guest');
      }
    } catch (err) {
      setError('Failed to check in guest');
      setIsConnected(false);
    }
  };

  const checkOutGuest = async (roomNumber: string) => {
    try {
      setError(null);
      const room = rooms.find(r => r.room_number === roomNumber);
      const guestName = room?.current_guest?.name || 'Guest';
      
      const success = await firebaseCheckOutGuest(roomNumber);
      
      if (success) {
        // Show success notification
        if ('Notification' in window && Notification.permission === 'granted') {
          new Notification('Hotel A.S.K Grand', {
            body: `${guestName} checked out from Room ${roomNumber}`,
            icon: '/Logo.png',
          });
        }
      } else {
        throw new Error('Failed to check out guest');
      }
    } catch (err) {
      setError('Failed to check out guest');
      setIsConnected(false);
    }
  };

  // Request notification permission
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  return {
    // Data
    rooms,
    statistics,
    analytics,
    loading,
    error,
    isConnected,
    lastUpdate,
    
    // Actions
    updateRoomStatus,
    checkInGuest,
    checkOutGuest,
    refreshData,
    
    // Utilities
    clearError: () => setError(null)
  };
}
